import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';

interface DeleteConfirmDialogProps {
  visible: boolean;
  onCancel: () => void;
  onDelete: () => void;
  message?: string;
  header?: string;
  testId?: string;
  loading?: boolean;
  /** Confirm button text. Defaults to the delete wording; set it when the
   *  dialog confirms something else, so the button never says Delete for an
   *  action that does not delete. */
  confirmLabel?: string;
  loadingLabel?: string;
  confirmSeverity?: 'danger' | 'success' | 'warning' | 'info';
}

/**
 * Reusable confirmation dialog for delete operations.
 *
 * @example
 * <DeleteConfirmDialog
 *   visible={deleteDialogOpen}
 *   onCancel={() => setDeleteDialogOpen(false)}
 *   onDelete={handleDeleteConfirm}
 *   message="Are you sure you want to delete this value set? This action cannot be undone."
 * />
 */
const DeleteConfirmDialog = ({
  visible,
  onCancel,
  onDelete,
  message = 'Are you sure you want to delete this item? This action cannot be undone.',
  header = 'Confirm Delete',
  testId = 'DeleteConfirmDialog',
  loading = false,
  confirmLabel = 'Delete',
  loadingLabel = 'Deleting...',
  confirmSeverity = 'danger',
}: DeleteConfirmDialogProps) => {
  const footerContent = (
  <div className="flex justify-end gap-3">
      <Button label="Cancel" severity="secondary" outlined size="small" onClick={onCancel} disabled={loading} />
      <Button label={loading ? loadingLabel : confirmLabel} severity={confirmSeverity} size="small" onClick={onDelete} disabled={loading} icon={loading ? 'pi pi-spin pi-spinner' : undefined} />
  </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onCancel}
      header={header}
      footer={footerContent}
      style={{ width: '420px' }}
      className="delete-confirm-dialog"
    >
      <div data-testid={testId} className="flex items-start gap-3">
        <i className="pi pi-exclamation-triangle text-amber-500 flex-shrink-0 text-2xl" />
        <p className="text-ink-muted m-0 text-sm leading-relaxed">
          {message}
        </p>
      </div>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
